import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Box,
  Typography,
} from "@mui/material";
import { accessRequestAPI } from "../../services/api";
import { useSnackbar } from "../../contexts/SnackbarContext";

const AccessRequestDialog = ({
  open,
  onClose,
  bookId,
  totalPages,
  existingRequest = null,
  onRequestSubmitted = () => {}, // Add callback for when request is submitted
}) => {
  const [requestType, setRequestType] = useState(
    existingRequest?.request_type || "full"
  );
  const [sectionStart, setSectionStart] = useState(
    existingRequest?.section_start || ""
  );
  const [sectionEnd, setSectionEnd] = useState(
    existingRequest?.section_end || ""
  );
  const [reason, setReason] = useState(existingRequest?.reason || "");
  const [submitting, setSubmitting] = useState(false);
  const isEditing = Boolean(existingRequest);
  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!reason.trim()) {
      showSnackbar(t("accessRequest.validation.reasonRequired"), "error");
      return;
    }

    if (requestType === "section") {
      const start = parseInt(sectionStart);
      const end = parseInt(sectionEnd);

      if (!start || !end || start < 1 || end > totalPages || start > end) {
        showSnackbar(t("accessRequest.validation.invalidPageRange"), "error");
        return;
      }
    }

    setSubmitting(true);

    try {
      const requestData = {
        request_type: requestType,
        section_start:
          requestType === "section" ? parseInt(sectionStart) : null,
        section_end: requestType === "section" ? parseInt(sectionEnd) : null,
        reason,
      };

      let response;
      if (isEditing) {
        response = await accessRequestAPI.update(
          existingRequest.id,
          requestData
        );
        console.log("Access request updated:", response.data);
        showSnackbar(t("accessRequest.messages.updateSuccess"), "success");
      } else {
        response = await accessRequestAPI.create(bookId, requestData);
        console.log("Access request created:", response.data);
        showSnackbar(t("accessRequest.messages.createSuccess"), "success");
      }

      // Notify parent component that a request was submitted
      onRequestSubmitted(response.data.request);
      handleClose();
    } catch (error) {
      showSnackbar(t("accessRequest.messages.submitError"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setRequestType("full");
    setSectionStart("");
    setSectionEnd("");
    setReason("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {isEditing
            ? t("accessRequest.dialog.editTitle")
            : t("accessRequest.dialog.requestTitle")}
        </DialogTitle>
        <DialogContent>
          {isEditing && (
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ mb: 2 }}
            >
              {t("accessRequest.dialog.status")}:{" "}
              {t(`accessRequest.status.${existingRequest.status}`)}
            </Typography>
          )}
          <Box sx={{ mt: 2 }}>
            <FormControl component="fieldset">
              <Typography variant="subtitle2" gutterBottom>
                {t("accessRequest.form.accessType")}
              </Typography>
              <RadioGroup
                row
                value={requestType}
                onChange={(e) => setRequestType(e.target.value)}
              >
                <FormControlLabel
                  value="full"
                  control={<Radio />}
                  label={t("accessRequest.form.fullBook")}
                />
                <FormControlLabel
                  value="section"
                  control={<Radio />}
                  label={t("accessRequest.form.specificPages")}
                />
              </RadioGroup>
            </FormControl>

            {requestType === "section" && (
              <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
                <TextField
                  label={t("accessRequest.form.startPage")}
                  type="number"
                  value={sectionStart}
                  onChange={(e) => setSectionStart(e.target.value)}
                  inputProps={{ min: 1, max: totalPages }}
                  required
                  fullWidth
                />
                <TextField
                  label={t("accessRequest.form.endPage")}
                  type="number"
                  value={sectionEnd}
                  onChange={(e) => setSectionEnd(e.target.value)}
                  inputProps={{ min: 1, max: totalPages }}
                  required
                  fullWidth
                />
              </Box>
            )}

            <TextField
              label={t("accessRequest.form.reasonLabel")}
              multiline
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              fullWidth
              sx={{ mt: 2 }}
              placeholder={t("accessRequest.form.reasonPlaceholder")}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t("common.cancel")}</Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={submitting}
          >
            {t("accessRequest.form.submit")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AccessRequestDialog;
